<template>
  <v-tooltip v-if="showChatButton" location="top center">
    <template #activator="{ props: tooltip }">
      <v-btn-accent
        elevation="4"
        class="copilot-btn"
        data-testid="chatContainer.toolbar-button"
        v-bind="tooltip"
        icon
        @click="openChat"
      >
        <v-icon color="white">mdi-robot-outline</v-icon>
      </v-btn-accent>
    </template>
    <span>Copilot</span>
  </v-tooltip>
  <v-card
    ref="chatContainerRef"
    elevation="6"
    class="copilot-card"
    :style="{
      right: modelValue ? '20px' : `calc(-${containerWidth} - 10px)`,
      resize: 'horizontal',
      transition: 'right 0.4s ease-in-out',
      width: containerWidth,
      direction: 'rtl',
      'max-width': '60%',
      'min-width': '250px',
    }"
    @transitionend="onTransitionEnd"
  >
    <div
      :style="{
        height: '100%',
        direction: 'ltr',
      }"
    >
      <v-toolbar style="height: 49px" density="compact" color="toolbar" flat>
        <v-toolbar-title class="text-font">Copilot</v-toolbar-title>
        <!-- <v-tooltip location="top center">
          <template #activator="{ props: tooltip }">
            <v-btn-icon
              class="ma-2"
              size="x-small"
              icon="mdi-delete"
              v-bind="tooltip"
              :disabled="
                loading || acceptedAI !== 'true' || messages.length < 2
              "
              @click="
                () => {
                  llmInput.blur();
                  showDeleteDialog = true;
                }
              "
            >
            </v-btn-icon>
          </template>
          <span>Delete Chat</span>
        </v-tooltip> -->
        <v-tooltip location="top center">
          <template #activator="{ props: tooltip }">
            <v-btn-icon icon="mdi-close" v-bind="tooltip" size="x-small" @click="closeChat"> </v-btn-icon>
          </template>
          <span>Close</span>
        </v-tooltip>
      </v-toolbar>
      <splitpanes class="default-theme split-panes-container" horizontal>
        <pane size="70" min-size="40">
          <v-card ref="messageContainer" class="chat-bubbles" rounded="0">
            <chat-bubble
              v-for="(message, index) in messages.filter((el) => {
                return !el.hideForUser;
              })"
              :key="index"
              :message="message"
              class="mb-3"
            />
          </v-card>
        </pane>
        <pane class="chat-input-container" size="30" min-size="20">
          <v-textarea
            ref="llmInput"
            v-model="inputMessage"
            rounded="0"
            class="chat-input"
            label="Type your message"
            rows="2"
            max-rows="2"
            no-resize
            hide-details
            density="compact"
            color="contrast-grey-high"
            bg-color="contrast-grey-low"
            :disabled="loading || acceptedAI !== 'true'"
            @keydown.enter.exact="handleEnterKey"
        /></pane>
      </splitpanes>
      <v-btn-primary
        class="corner-button"
        icon="mdi-send"
        elevation="4"
        :disabled="loading || acceptedAI !== 'true'"
        @click="submit"
      >
      </v-btn-primary>
    </div>
  </v-card>
  <chat-delete-dialog
    v-if="showDeleteDialog"
    @cancel="
      () => {
        showDeleteDialog = false;
        nextTick(() => {
          llmInput.focus();
        });
      }
    "
    @delete="
      () => {
        showDeleteDialog = false;
        reset();
      }
    "
  ></chat-delete-dialog>
  <chat-terms-of-use-dialog
    v-if="showTermsOfUseDialog"
    @cancel="
      () => {
        showTermsOfUseDialog = false;
      }
    "
    @accept="
      () => {
        showTermsOfUseDialog = false;
        acceptedAI = 'true';
        openChat();
      }
    "
  ></chat-terms-of-use-dialog>
</template>

<script setup lang="ts">
import { ref, onMounted, inject, nextTick, watch, onBeforeUnmount } from "vue";
import { Message } from "./Message";
import ChatBubble from "./ChatBubble.vue";
import ChatDeleteDialog from "./ChatDeleteDialog.vue";
import ChatTermsOfUseDialog from "./ChatTermsOfUseDialog.vue";
import { CopilotService } from "../services/copilot/copilot-service";
import { Splitpanes, Pane } from "splitpanes";
import ModelLoader from "../services/model-loader";
import RappidContext from "../rappid/rappid-context";
import { GraphLayouter } from "../services/layout/graph-layouter";
import { messageStore } from "./messageStore";
import { storeToRefs } from "pinia";
import LocalStorage from "../services/persistance/local-storage";
import { ModelEventBus } from "../../event-bus";
import { PreferenceStore } from "../services/preference-store";
import * as joint from "@clientio/rappid";
import { load } from "ace-builds/src-noconflict/ext-emmet";
import { getStatechart } from "../services/model-helper";

const compProps = defineProps<{
  modelValue: boolean;
}>();

const emit = defineEmits(["update:modelValue", "resize"]);

const { messages } = storeToRefs(messageStore());
const inputMessage = ref<string>("");

const copilotService = inject<CopilotService>("copilotService");
const localStorage: LocalStorage = inject<LocalStorage>("localStorage");

const modelLoader: ModelLoader = inject<ModelLoader>("modelLoader");
const rappidContext: RappidContext = inject<RappidContext>("rappidContext");
const layoutService: GraphLayouter = inject<GraphLayouter>("layoutService");

const loading = ref<boolean>(false);
const messageContainer = ref(null);
const llmInput = ref(null);
const showDeleteDialog = ref<boolean>(false);
const showChatButton = ref<boolean>(true);

const showTermsOfUseDialog = ref<boolean>(false);
const acceptedAI = ref<string>(localStorage.get("acceptedAI"));

const chatContainerRef = ref(null);
const chatContainer = ref<HTMLElement>();
const resizeHandler = ref<ResizeObserver>();
const containerWidth = ref<string>("300px");
const preferenceStore = PreferenceStore.INSTANCE;

onMounted(async () => {
  reset();
  ModelEventBus.on("modelLoaded", (payload) => {
    // If the Copilot triggers the loading, we don't want no reset
    if (payload.source !== "ChatContainer") {
      reset();
    }
  });
  chatContainer.value = chatContainerRef.value.$el as HTMLElement;
  resizeHandler.value = new ResizeObserver(() => {
    containerWidth.value = `${chatContainer.value.clientWidth}px`;
    emit("resize");
  });
  resizeHandler.value.observe(chatContainer.value);
});

onBeforeUnmount(() => {
  ModelEventBus.off("modelLoaded", reset);
  resizeHandler.value.disconnect();
});

function openChat() {
  if (acceptedAI.value === "true") {
    emit("update:modelValue", true);
  } else {
    showTermsOfUseDialog.value = true;
  }
}

function closeChat() {
  emit("update:modelValue", false);
}

function onTransitionEnd() {
  // on hide chat, show button only at end of transition
  if (!compProps.modelValue) {
    showChatButton.value = true;
  }
}

watch(
  () => compProps.modelValue,
  (value: boolean) => {
    // on show chat, hide button from start of transition
    if (value) {
      showChatButton.value = false;
    }
  }
);

function addErrorMsg() {
  messageStore().addMessage({
    type: "assistant",
    text: "Sorry, I don't understand what to do.",
    error: true,
  });
}

function reset() {
  messageStore().clearMessages();
  messageStore().addMessage({
    type: "assistant",
    text: "How can I help you today?",
  });
  inputMessage.value = "";
  nextTick(() => {
    llmInput.value?.focus();
  });
}

function loadModel(modelFromCopilt: string) {
  const batchName = "load-with-undo";
  try {
    // Prevent Router from listening during graph manipulation.
    rappidContext.graph.startBatch(batchName);
    rappidContext.commandManager.initBatchCommand();
    rappidContext.paper.freeze();
    const tmpGraph = new joint.dia.Graph({}, { cellNamespace: joint.shapes });
    tmpGraph.fromJSON(JSON.parse(modelFromCopilt), { mergeArrays: true, silent: true });
    const newCells = tmpGraph.getCells();
    rappidContext.graph.clear();
    rappidContext.graph.addCells(newCells, { mergeArrays: true, async: false });
    // For some reason, back-references are not updated if cells are moved from graph to graph
    newCells.forEach((cell) => {
      cell.graph = rappidContext.graph;
      const parent = cell.getParentCell();
      if (parent && !parent.getEmbeddedCells()?.includes(cell)) {
        parent.embed(cell);
      }
    });
    rappidContext.paper.once("render:done", () => {
      rappidContext.routerController.setRouterForAllLinks("smart (obstacle-avoid)");
      layoutService.layout(rappidContext);
      rappidContext.commandManager.storeBatchCommand();
      rappidContext.graph.stopBatch(batchName);
    });
    rappidContext.paper.unfreeze();

    // It's not technically a load,
    // but we need to emit this so that several components are synched.
    ModelEventBus.emit("modelLoaded", {
      statechart: getStatechart(rappidContext.paper.model),
      source: "ChatContainer",
    });
  } catch (error) {
    rappidContext.paper.unfreeze();
    rappidContext.commandManager.storeBatchCommand();
    rappidContext.graph.stopBatch(batchName);
    throw error;
  }
}

async function submit() {
  if (inputMessage.value.trim() !== "" && !loading.value) {
    loading.value = true;
    //only sending model if it has contents.
    if (rappidContext.paper.model.getCells().length > 1) {
      messageStore().addMessage({
        type: "user",
        text: "That is the current model, to which my next question refers.",
        error: false,
        model: JSON.stringify(rappidContext.paper.model),
        sml: undefined,
        hideForUser: true,
      });
    }

    messageStore().addMessage({
      type: "user",
      text: inputMessage.value,
    });
    scrollLastChatBubbleIntoView();
    await copilotService
      .prompt(
        inputMessage.value,
        messages.value
          .filter((el) => {
            return el.type !== "assistant-loading";
          })
          .slice(0, -1)
      )
      .then(async (result) => {
        if (result.model) {
          loadModel(result.model);
          messageStore().addMessage({
            type: "assistant",
            text: result.text,
            error: false,
            model: result.model,
            sml: result.sml,
          });
        } else if (result.text) {
          messageStore().addMessage({
            type: "assistant",
            text: result.text,
            sml: result.sml,
          });
        } else {
          addErrorMsg();
        }
      })
      .catch((err) => {
        addErrorMsg();
      });
    loading.value = false;
    scrollLastChatBubbleIntoView();
    inputMessage.value = "";
  }
  nextTick(() => {
    llmInput.value.focus();
  });
}

function handleEnterKey(event: KeyboardEvent) {
  submit();
  event.preventDefault();
}

function scrollLastChatBubbleIntoView() {
  nextTick(() => {
    const container = messageContainer.value.$el;
    container.scrollTo({
      top: container.scrollHeight,
      behavior: "smooth",
    });
  });
}
</script>

<style>
.chat-input-container .v-input__control,
.chat-input-container .v-field__input {
  height: 100%;
  width: 100%;
}

.chat-input-container .v-field__field {
  padding-top: 10px;
  padding-bottom: 10px;
}

.splitpanes__splitter {
  background: rgba(var(--v-theme-contrast-grey-medium)) !important;
}

.splitpanes__pane {
  justify-content: center;
  align-items: center;
  display: flex;
  position: relative;
  background: rgba(var(--v-theme-toolbar)) !important;
}
</style>
<style scoped>
.copilot-btn {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 10;
}

.split-panes-container {
  height: calc(100% - 49px);
}

.chat-input-container .v-input--horizontal {
  display: flex;
}

.chat-bubbles {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow-y: scroll;
}
.chat-input-container {
  height: 100%;
  width: 100%;
}
.chat-input {
  height: 100%;
  width: 100%;
}
.corner-button {
  position: absolute;
  bottom: 20px;
  right: 10px;
}
</style>
